/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useRef } from 'react';

import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { biaxialWidgetUpdateHorizontalPositionAction } from '../../../store/biaxialwidget/biaxialwidget.actions';
import {
    BWHListType,
    SCROLL_DIRECTION,
    TVerticalItems,
} from '../../../store/biaxialwidget/biaxialwidget.props';
import { biaxialWidgetUpdateHorizontalItems } from '../../../store/biaxialwidget/biaxialwidget.reducers';
import {
    onKeyDownBiaxialWidget,
    onKeyLeftBiaxialWidget,
    onKeyRightBiaxialWidget,
    onKeyUpBiaxialWidget,
} from '../../../store/biaxialwidget/navigation/biaxialwidgetnavigation.reducers';
import { BWHorizontalList } from './BWHorizontalList';
import { BWInteractiveLayer } from './BWInteractiveLayer';

import './BiaxialWidget.scss';

const TURBO_SCROLL_ANIMATION_DURATION = 300;

const BiaxialWidget: FunctionComponent = (): JSX.Element => {
    const { verticalItems, verticalPosition, isTurboScroll, turboScrollDirection } =
        useTivoSelector((state: RootState) => state.biaxialWidget);

    const horizontalListRef = useRef<HTMLDivElement | null>(null);

    const dispatch = useTivoDispatch();

    useEffect(() => {
        let turboScrollTimeout: NodeJS.Timeout | null = null;
        if (isTurboScroll) {
            const isVerticalDirection =
                turboScrollDirection === SCROLL_DIRECTION.UP ||
                turboScrollDirection === SCROLL_DIRECTION.DOWN;
            if (isVerticalDirection) {
                dispatch(biaxialWidgetUpdateHorizontalPositionAction(0));
                dispatch(
                    biaxialWidgetUpdateHorizontalItems({
                        isEmpty: false,
                        listItems: [],
                        listType: BWHListType.DEFAULT,
                    }),
                );
            }
            turboScrollTimeout = setInterval(() => {
                doTurboScroll();
            }, TURBO_SCROLL_ANIMATION_DURATION);
        }

        return () => {
            if (turboScrollTimeout) {
                clearInterval(turboScrollTimeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTurboScroll]);

    const doTurboScroll = () => {
        switch (turboScrollDirection) {
            case SCROLL_DIRECTION.DOWN:
                dispatch(onKeyDownBiaxialWidget());
                break;
            case SCROLL_DIRECTION.LEFT:
                dispatch(onKeyLeftBiaxialWidget());
                break;
            case SCROLL_DIRECTION.RIGHT:
                dispatch(onKeyRightBiaxialWidget());
                break;
            case SCROLL_DIRECTION.UP:
                dispatch(onKeyUpBiaxialWidget());
                break;

            default:
        }
    };

    const populateVIContainer = (items: TVerticalItems, listClass: string) => {
        return (
            <div className={listClass}>
                {items.map((item, idx) => (
                    <div key={`${item.listTitle}${idx}`} className="vertical-item">
                        {item.listTitle}
                    </div>
                ))}
            </div>
        );
    };

    const middleItemList = (
        <div className="middle-item-list">
            <div className="selected-vertical-item">
                {verticalItems[verticalPosition]?.displayTitle}
            </div>
            <BWHorizontalList />
        </div>
    );

    return (
        <div className="bw-container">
            <div className="top-vertical-items-container">
                {populateVIContainer(verticalItems.slice(0, verticalPosition), 'top-item-list')}
            </div>
            <div className="middle-vertical-items-container" ref={horizontalListRef}>
                {middleItemList}
            </div>
            <div className="bottom-vertical-items-container">
                {populateVIContainer(
                    verticalItems.slice(verticalPosition + 1, verticalItems.length),
                    'bottom-item-list',
                )}
            </div>
            <BWInteractiveLayer middleLayerHeight={horizontalListRef.current?.scrollHeight || 0} />
        </div>
    );
};

export default BiaxialWidget;
